import { 
  Globe, 
  Phone, 
  Zap,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  SubmissionLog, 
  SubmissionLaneType,
  LANE_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
} from '@/lib/submission/types';
import { format } from 'date-fns';

interface SubmissionLogListProps {
  logs: SubmissionLog[];
  onRetry?: (logId: string) => Promise<boolean>;
}

const LANE_ICONS: Record<SubmissionLaneType, React.ReactNode> = {
  FHIR_PAS: <Zap className="h-4 w-4 text-primary" />,
  PORTAL: <Globe className="h-4 w-4 text-blue-500" />,
  FAX_PHONE: <Phone className="h-4 w-4 text-orange-500" />,
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  PENDING: <Clock className="h-4 w-4" />,
  SUBMITTED: <Clock className="h-4 w-4" />,
  CONFIRMED: <CheckCircle2 className="h-4 w-4" />,
  FAILED: <XCircle className="h-4 w-4" />,
  RETRYING: <RefreshCw className="h-4 w-4 animate-spin" />,
};

export function SubmissionLogList({ logs, onRetry }: SubmissionLogListProps) {
  if (logs.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm">
        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No submission history</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground">Submission History</h4>
      
      <div className="space-y-2">
        {logs.map((log) => (
          <div 
            key={log.id}
            className="bg-muted/30 rounded-lg p-3 space-y-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {LANE_ICONS[log.lane]}
                <span className="text-sm font-medium">{LANE_LABELS[log.lane]}</span>
              </div>
              <Badge className={`text-xs ${STATUS_COLORS[log.status]}`}>
                {STATUS_ICONS[log.status]}
                <span className="ml-1">{STATUS_LABELS[log.status]}</span>
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>
                <span className="font-medium">Executed:</span>{' '}
                {format(new Date(log.executed_at), 'MMM d, yyyy h:mm a')}
              </div>
              {log.confirmed_at && (
                <div>
                  <span className="font-medium">Confirmed:</span>{' '}
                  {format(new Date(log.confirmed_at), 'MMM d, yyyy h:mm a')}
                </div>
              )}
            </div>

            {/* Reference Numbers */}
            {(log.submission_reference_id || log.payer_confirmation_number) && (
              <div className="text-xs">
                {log.submission_reference_id && (
                  <div>
                    <span className="text-muted-foreground">Reference:</span>{' '}
                    <span className="font-mono">{log.submission_reference_id}</span>
                  </div>
                )}
                {log.payer_confirmation_number && log.payer_confirmation_number !== log.submission_reference_id && (
                  <div>
                    <span className="text-muted-foreground">Confirmation:</span>{' '}
                    <span className="font-mono">{log.payer_confirmation_number}</span>
                  </div>
                )}
              </div>
            )}

            {/* Proof Link */}
            {(log.proof_url || log.proof_file_path) && (
              <div className="text-xs">
                <a 
                  href={log.proof_url || log.proof_file_path || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  View Proof
                </a>
              </div>
            )}

            {/* Error Message */}
            {log.error_message && (
              <div className="text-xs text-red-500 bg-red-500/10 rounded p-2">
                {log.error_message}
              </div>
            )}

            {/* Retry Button */}
            {log.status === 'FAILED' && onRetry && (
              <div className="pt-1">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => onRetry(log.id)}
                  className="text-xs h-7"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
